import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from 'semantic-ui-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StatusContext } from '../context/Status';
import { UserContext } from '../context/User';
import {
  API,
  getCachedStatus,
  getLogo,
  getSystemName,
  showSuccess,
} from '../helpers';
import './Header.css';

const NAV_ITEMS = [
  { name: 'header.channel', to: '/channel', icon: 'sitemap', admin: true },
  { name: 'header.pool', to: '/pool', icon: 'server', admin: true },
  { name: 'header.idpool', to: '/idpool', icon: 'id badge', admin: true },
  { name: 'header.token', to: '/token', icon: 'key' },
  { name: 'header.playground', to: '/playground', icon: 'lab', admin: true },
  { name: 'header.user', to: '/user', icon: 'user', admin: true },
  { name: 'header.dashboard', to: '/dashboard', icon: 'chart bar' },
  { name: 'header.log', to: '/log', icon: 'book' },
  { name: 'header.setting', to: '/setting', icon: 'setting' },
];

const Header = () => {
  const { t, i18n } = useTranslation();
  const [userState, userDispatch] = useContext(UserContext);
  const [statusState] = useContext(StatusContext);
  const navigate = useNavigate();
  const location = useLocation();
  const menuButtonRef = useRef(null);
  const headerRef = useRef(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const status = statusState?.status || getCachedStatus();
  const user = userState.user;
  const systemName = status.system_name || getSystemName();
  const logo = status.logo || getLogo();
  const isAdmin = Number(user?.role) >= 10;

  const navItems = useMemo(() => {
    const items = [...NAV_ITEMS];
    if (status.chat_link) {
      items.splice(1, 0, {
        name: 'header.chat',
        to: '/chat',
        icon: 'comments',
      });
    }
    return items.filter((item) => !item.admin || isAdmin);
  }, [isAdmin, status.chat_link]);

  useEffect(() => {
    setDrawerOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen && !userMenuOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (userMenuOpen) {
        setUserMenuOpen(false);
      } else {
        setDrawerOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    const handlePointerDown = (event) => {
      if (userMenuOpen && !headerRef.current?.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [drawerOpen, userMenuOpen]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [drawerOpen]);

  const logout = async () => {
    setDrawerOpen(false);
    setUserMenuOpen(false);
    try {
      await API.get('/api/user/logout');
    } finally {
      localStorage.removeItem('user');
      userDispatch({ type: 'logout' });
      showSuccess(t('header.logout_success'));
      navigate('/login', { replace: true });
    }
  };

  const changeLanguage = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  return (
    <>
      <header className='studio-header' ref={headerRef}>
        <div className='studio-header-inner'>
          <Link to='/' className='studio-brand' aria-label={systemName}>
            <span className='studio-brand-logo'>
              <img src={logo} alt='' />
            </span>
            <span className='studio-brand-copy'>
              <strong>{systemName}</strong>
              <span>{t('header.studio')}</span>
            </span>
          </Link>

          <div
            id='studio-navigation'
            className={`studio-navigation${drawerOpen ? ' is-open' : ''}`}
          >
            <div className='studio-drawer-heading'>
              <span>{t('header.navigation')}</span>
              <button
                type='button'
                className='studio-icon-button'
                onClick={() => setDrawerOpen(false)}
                aria-label={t('header.close_navigation')}
              >
                <Icon name='close' />
              </button>
            </div>

            <nav
              className='studio-nav-links'
              aria-label={t('header.navigation')}
            >
              {navItems.map((item) => {
                const active =
                  location.pathname === item.to ||
                  location.pathname.startsWith(`${item.to}/`);
                return (
                  <Link
                    key={item.name}
                    to={item.to}
                    className={`studio-nav-link${active ? ' is-active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon name={item.icon} />
                    <span>{t(item.name)}</span>
                  </Link>
                );
              })}
            </nav>

            <div className='studio-header-actions'>
              <label className='studio-language'>
                <Icon name='language' />
                <span className='sr-only'>{t('header.language')}</span>
                <select
                  value={(i18n.resolvedLanguage || i18n.language).split('-')[0]}
                  onChange={changeLanguage}
                >
                  <option value='zh'>中文</option>
                  <option value='en'>English</option>
                </select>
              </label>

              <div className='studio-user-menu'>
                <button
                  type='button'
                  className='studio-user-trigger'
                  onClick={() => setUserMenuOpen((open) => !open)}
                  aria-expanded={userMenuOpen}
                  aria-haspopup='menu'
                >
                  <span className='studio-user-avatar' aria-hidden='true'>
                    {(user?.username || 'U').slice(0, 1).toUpperCase()}
                  </span>
                  <span className='studio-user-name'>{user?.username}</span>
                  <Icon name={userMenuOpen ? 'angle up' : 'angle down'} />
                </button>
                {userMenuOpen && (
                  <div className='studio-user-popover' role='menu'>
                    <div className='studio-user-summary'>
                      <strong>{user?.username}</strong>
                      <span>
                        {isAdmin
                          ? t('header.administrator')
                          : t('header.member')}
                      </span>
                    </div>
                    <button type='button' onClick={logout} role='menuitem'>
                      <Icon name='sign-out' />
                      {t('header.logout')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            ref={menuButtonRef}
            type='button'
            className='studio-menu-toggle'
            onClick={() => setDrawerOpen((open) => !open)}
            aria-label={t('header.open_navigation')}
            aria-expanded={drawerOpen}
            aria-controls='studio-navigation'
          >
            <Icon name={drawerOpen ? 'close' : 'bars'} />
          </button>
        </div>
      </header>
      {drawerOpen && (
        <button
          type='button'
          className='studio-drawer-backdrop'
          aria-label={t('header.close_navigation')}
          onClick={() => {
            setDrawerOpen(false);
            menuButtonRef.current?.focus();
          }}
        />
      )}
    </>
  );
};

export default Header;
